import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowLeft, FaClock, FaUsers, FaCalendar, FaMapMarkerAlt,
  FaCheckCircle, FaCertificate, FaBook, FaLaptop, FaGraduationCap,
  FaChalkboardTeacher, FaStar, FaDownload, FaShareAlt, FaEnvelope,
  FaSpinner, FaExclamationTriangle, FaSearch
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../config/api';

const FormationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedFormations, setRelatedFormations] = useState([]);

  useEffect(() => {
    const fetchFormation = async () => {
      setLoading(true);
      setError(null);
      try {
        // Rechercher la formation par slug
        const response = await axios.get(`${API_URL}/formations`, {
          params: { search: slug }
        });
        
        const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
        const found = data.find(f => f.slug === slug);
        
        if (found) {
          setFormation(found);
          // Charger formations similaires (même domaine)
          if (found.domaine) {
            const relatedRes = await axios.get(`${API_URL}/formations`, {
              params: { domaine: found.domaine, limit: 4 }
            });
            const relatedData = Array.isArray(relatedRes.data) ? relatedRes.data : (relatedRes.data.data || []);
            setRelatedFormations(relatedData.filter(f => f.slug !== slug).slice(0, 3));
          }
        } else {
          setError('Formation non trouvée');
        }
      } catch (err) {
        console.error('Erreur chargement formation:', err);
        setError('Erreur lors du chargement de la formation');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchFormation();
    }
  }, [slug]);

  const handleInscription = () => {
    navigate('/inscriptionformation', { state: { formation: formation?.titre } });
  };

  const handleDevis = () => {
    navigate('/ledevis', { state: { formation: formation?.titre } });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={styles.loadingContainer}>
          <FaSpinner style={styles.spinner} />
          <p style={styles.loadingText}>Chargement de la formation...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !formation) {
    return (
      <>
        <Header />
        <div style={styles.errorContainer}>
          <FaExclamationTriangle style={styles.errorIcon} />
          <h2 style={styles.errorTitle}>{error || 'Formation non trouvée'}</h2>
          <p style={styles.errorText}>
            La formation que vous recherchez n'existe pas ou a été supprimée.
          </p>
          <div style={styles.errorActions}>
            <button style={styles.btnPrimary} onClick={() => navigate('/nos-formations')}>
              <FaSearch /> Voir toutes les formations
            </button>
            <button style={styles.btnSecondary} onClick={() => navigate('/')}>
              <FaArrowLeft /> Retour à l'accueil
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const getDomainColor = (domain) => {
    const colors = {
      management: '#198754',
      informatique: '#0d6efd',
      finance: '#6f42c1',
      rh: '#fd7e14',
      marketing: '#dc3545',
      droit: '#20c997',
      qualite: '#ffc107'
    };
    const key = (domain || '').toLowerCase();
    for (const [k, v] of Object.entries(colors)) {
      if (key.includes(k)) return v;
    }
    return '#fd7e14';
  };

  const domainColor = getDomainColor(formation.domaine);

  return (
    <>
      <Header />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        .formation-detail-page * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-in {
          animation: fadeIn 0.6s ease-out;
        }
      `}</style>

      <div className="formation-detail-page" style={styles.page}>
        {/* Breadcrumb & Back */}
        <div style={styles.breadcrumb}>
          <div style={styles.container}>
            <button style={styles.btnBack} onClick={() => navigate(-1)}>
              <FaArrowLeft /> Retour
            </button>
            <div style={styles.breadcrumbPath}>
              <Link to="/" style={styles.breadcrumbLink}>Accueil</Link>
              <span style={styles.breadcrumbSep}>/</span>
              <Link to="/nos-formations" style={styles.breadcrumbLink}>Formations</Link>
              <span style={styles.breadcrumbSep}>/</span>
              <span style={styles.breadcrumbCurrent}>{formation.titre}</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div style={{ ...styles.hero, background: `linear-gradient(135deg, ${domainColor}15 0%, ${domainColor}08 100%)` }}>
          <div style={styles.container} className="animate-in">
            <div style={styles.heroContent}>
              <div style={styles.heroLeft}>
                <div style={{ ...styles.badge, background: `${domainColor}20`, color: domainColor }}>
                  <FaGraduationCap /> {formation.domaine || 'Formation'}
                </div>
                <h1 style={styles.title}>{formation.titre}</h1>
                <p style={styles.subtitle}>{formation.description || formation.objectif}</p>
                
                <div style={styles.metaGrid}>
                  <div style={styles.metaItem}>
                    <FaClock style={{ color: domainColor }} />
                    <span>{formation.duree || 'Sur mesure'}</span>
                  </div>
                  <div style={styles.metaItem}>
                    <FaUsers style={{ color: domainColor }} />
                    <span>{formation.niveau || 'Tous niveaux'}</span>
                  </div>
                  {formation.modalite && (
                    <div style={styles.metaItem}>
                      <FaLaptop style={{ color: domainColor }} />
                      <span>{formation.modalite}</span>
                    </div>
                  )}
                  {formation.lieu && (
                    <div style={styles.metaItem}>
                      <FaMapMarkerAlt style={{ color: domainColor }} />
                      <span>{formation.lieu}</span>
                    </div>
                  )}
                </div>

                <div style={styles.ctaGroup}>
                  <button style={{ ...styles.btnCta, background: domainColor }} onClick={handleInscription}>
                    <FaCalendar /> S'inscrire maintenant
                  </button>
                  <button style={styles.btnCtaSecondary} onClick={handleDevis}>
                    <FaEnvelope /> Demander un devis
                  </button>
                </div>
              </div>

              {formation.image && (
                <div style={styles.heroRight}>
                  <img src={formation.image} alt={formation.titre} style={styles.heroImage} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div style={styles.container}>
          <div style={styles.contentGrid}>
            {/* Main Content */}
            <div style={styles.mainContent}>
              {/* Objectifs */}
              {formation.objectifs && (
                <section style={styles.section} className="animate-in">
                  <h2 style={styles.sectionTitle}>
                    <FaCheckCircle style={{ color: domainColor }} /> Objectifs de la formation
                  </h2>
                  <div style={styles.objectifsList}>
                    {(Array.isArray(formation.objectifs) ? formation.objectifs : formation.objectifs.split('\n')).map((obj, i) => (
                      <div key={i} style={styles.objectifItem}>
                        <FaCheckCircle style={{ color: domainColor, fontSize: 16, flexShrink: 0 }} />
                        <span>{obj}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Programme */}
              {formation.programme && (
                <section style={styles.section} className="animate-in">
                  <h2 style={styles.sectionTitle}>
                    <FaBook style={{ color: domainColor }} /> Programme détaillé
                  </h2>
                  <div style={styles.programme}>
                    {(Array.isArray(formation.programme) ? formation.programme : formation.programme.split('\n')).map((item, i) => (
                      <div key={i} style={styles.programmeItem}>
                        <div style={{ ...styles.programmeNumber, background: `${domainColor}15`, color: domainColor }}>
                          {i + 1}
                        </div>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Public cible */}
              {formation.public_cible && (
                <section style={styles.section} className="animate-in">
                  <h2 style={styles.sectionTitle}>
                    <FaUsers style={{ color: domainColor }} /> Public ciblé
                  </h2>
                  <p style={styles.textContent}>{formation.public_cible}</p>
                </section>
              )}

              {/* Certification */}
              {formation.certification && (
                <section style={styles.section} className="animate-in">
                  <div style={{ ...styles.certificationCard, borderColor: domainColor }}>
                    <FaCertificate style={{ fontSize: 48, color: domainColor }} />
                    <div>
                      <h3 style={styles.certificationTitle}>Certification délivrée</h3>
                      <p style={styles.certificationText}>{formation.certification}</p>
                    </div>
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <aside style={styles.sidebar}>
              {/* Card Inscription */}
              <div style={styles.sidebarCard} className="animate-in">
                <h3 style={styles.sidebarCardTitle}>Prêt à vous lancer ?</h3>
                <div style={styles.priceTag}>
                  {formation.prix ? (
                    <>
                      <span style={styles.price}>{formation.prix} FCFA</span>
                      <span style={styles.priceSub}>par participant</span>
                    </>
                  ) : (
                    <span style={styles.priceOnDemand}>Prix sur demande</span>
                  )}
                </div>
                <button
                  style={{ ...styles.btnSidebar, background: domainColor }}
                  onClick={handleInscription}
                >
                  <FaCalendar /> S'inscrire
                </button>
                <button style={styles.btnSidebarOutline} onClick={handleDevis}>
                  <FaEnvelope /> Demander un devis
                </button>
                <button style={styles.btnSidebarOutline}>
                  <FaDownload /> Télécharger le programme
                </button>
              </div>

              {/* Card Info */}
              <div style={styles.sidebarCard} className="animate-in">
                <h3 style={styles.sidebarCardTitle}>Informations pratiques</h3>
                <div style={styles.infoList}>
                  <div style={styles.infoItem}>
                    <strong>Durée :</strong>
                    <span>{formation.duree || 'Flexible'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Format :</strong>
                    <span>{formation.modalite || 'Présentiel'}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <strong>Niveau :</strong>
                    <span>{formation.niveau || 'Tous niveaux'}</span>
                  </div>
                  {formation.lieu && (
                    <div style={styles.infoItem}>
                      <strong>Lieu :</strong>
                      <span>{formation.lieu}</span>
                    </div>
                  )}
                  <div style={styles.infoItem}>
                    <strong>Langue :</strong>
                    <span>Français</span>
                  </div>
                </div>
              </div>

              {/* Card Contact */}
              <div style={{ ...styles.sidebarCard, background: `${domainColor}10` }} className="animate-in">
                <h3 style={styles.sidebarCardTitle}>Besoin d'aide ?</h3>
                <p style={styles.contactText}>
                  Notre équipe est là pour répondre à vos questions
                </p>
                <Link to="/contact" style={{ ...styles.btnContact, borderColor: domainColor, color: domainColor }}>
                  <FaEnvelope /> Nous contacter
                </Link>
              </div>
            </aside>
          </div>

          {/* Formations similaires */}
          {relatedFormations.length > 0 && (
            <section style={styles.relatedSection}>
              <h2 style={styles.relatedTitle}>Formations similaires</h2>
              <div style={styles.relatedGrid}>
                {relatedFormations.map((f, i) => (
                  <Link
                    key={i}
                    to={`/formations/${f.slug}`}
                    style={styles.relatedCard}
                    className="animate-in"
                  >
                    <div style={{ ...styles.relatedBadge, background: `${getDomainColor(f.domaine)}20`, color: getDomainColor(f.domaine) }}>
                      {f.domaine}
                    </div>
                    <h3 style={styles.relatedCardTitle}>{f.titre}</h3>
                    <p style={styles.relatedCardDesc}>{f.description?.slice(0, 100)}...</p>
                    <div style={styles.relatedMeta}>
                      <span><FaClock /> {f.duree}</span>
                      {f.niveau && <span><FaUsers /> {f.niveau}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f8f9fa',
    paddingTop: 80,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: 20,
  },
  spinner: {
    fontSize: 48,
    color: '#fd7e14',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: 18,
    color: '#6c757d',
    fontWeight: 600,
  },
  errorContainer: {
    maxWidth: 600,
    margin: '80px auto',
    padding: '60px 40px',
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: 64,
    color: '#dc3545',
    marginBottom: 24,
  },
  errorTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: '#1a202c',
    marginBottom: 16,
  },
  errorText: {
    fontSize: 16,
    color: '#6c757d',
    marginBottom: 32,
    lineHeight: 1.6,
  },
  errorActions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  breadcrumb: {
    background: '#fff',
    borderBottom: '1px solid #e9ecef',
    padding: '16px 0',
  },
  container: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
  },
  btnBack: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: '#fff',
    border: '1.5px solid #dee2e6',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginBottom: 12,
  },
  breadcrumbPath: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: '#6c757d',
  },
  breadcrumbLink: {
    color: '#198754',
    textDecoration: 'none',
    fontWeight: 500,
  },
  breadcrumbSep: {
    color: '#dee2e6',
  },
  breadcrumbCurrent: {
    color: '#1a202c',
    fontWeight: 600,
  },
  hero: {
    padding: '60px 0',
    marginBottom: 60,
  },
  heroContent: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 1fr',
    gap: 60,
    alignItems: 'center',
  },
  heroLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    borderRadius: 50,
    fontSize: 14,
    fontWeight: 700,
    width: 'fit-content',
  },
  title: {
    fontSize: 48,
    fontWeight: 900,
    color: '#1a202c',
    lineHeight: 1.15,
    margin: 0,
  },
  subtitle: {
    fontSize: 18,
    lineHeight: 1.7,
    color: '#495057',
    margin: 0,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 15,
    fontWeight: 600,
    color: '#495057',
  },
  ctaGroup: {
    display: 'flex',
    gap: 12,
    marginTop: 12,
  },
  btnCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '16px 32px',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
  },
  btnCtaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '16px 32px',
    background: '#fff',
    border: '2px solid #198754',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 700,
    color: '#198754',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  heroRight: {
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  heroImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 380px',
    gap: 40,
    marginBottom: 80,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
  },
  section: {
    background: '#fff',
    borderRadius: 16,
    padding: 32,
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 24,
    fontWeight: 800,
    color: '#1a202c',
    marginBottom: 24,
  },
  objectifsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  objectifItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    fontSize: 16,
    lineHeight: 1.6,
    color: '#495057',
  },
  programme: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  programmeItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    fontSize: 16,
    lineHeight: 1.6,
    color: '#495057',
  },
  programmeNumber: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 800,
    flexShrink: 0,
  },
  textContent: {
    fontSize: 16,
    lineHeight: 1.8,
    color: '#495057',
  },
  certificationCard: {
    display: 'flex',
    gap: 24,
    padding: 32,
    background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
    borderRadius: 16,
    border: '2px solid',
    alignItems: 'center',
  },
  certificationTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: '#1a202c',
    marginBottom: 8,
  },
  certificationText: {
    fontSize: 15,
    color: '#495057',
    lineHeight: 1.6,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  sidebarCard: {
    background: '#fff',
    borderRadius: 16,
    padding: 28,
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  sidebarCardTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: '#1a202c',
    marginBottom: 20,
  },
  priceTag: {
    textAlign: 'center',
    padding: '24px 0',
    marginBottom: 24,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
  },
  price: {
    display: 'block',
    fontSize: 32,
    fontWeight: 900,
    color: '#198754',
  },
  priceSub: {
    display: 'block',
    fontSize: 14,
    color: '#6c757d',
    marginTop: 4,
  },
  priceOnDemand: {
    fontSize: 20,
    fontWeight: 700,
    color: '#495057',
  },
  btnSidebar: {
    width: '100%',
    padding: '14px 24px',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 10,
  },
  btnSidebarOutline: {
    width: '100%',
    padding: '14px 24px',
    background: 'transparent',
    border: '1.5px solid #dee2e6',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 10,
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 15,
    color: '#495057',
    paddingBottom: 16,
    borderBottom: '1px solid #e9ecef',
  },
  contactText: {
    fontSize: 15,
    color: '#6c757d',
    lineHeight: 1.6,
    marginBottom: 20,
  },
  btnContact: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '14px 24px',
    background: 'transparent',
    border: '2px solid',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'all 0.3s',
  },
  relatedSection: {
    marginBottom: 80,
  },
  relatedTitle: {
    fontSize: 32,
    fontWeight: 900,
    color: '#1a202c',
    marginBottom: 32,
    textAlign: 'center',
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
  relatedCard: {
    background: '#fff',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    textDecoration: 'none',
    transition: 'all 0.3s',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  relatedBadge: {
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    width: 'fit-content',
  },
  relatedCardTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1a202c',
    margin: 0,
  },
  relatedCardDesc: {
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 1.6,
  },
  relatedMeta: {
    display: 'flex',
    gap: 16,
    fontSize: 13,
    color: '#6c757d',
    marginTop: 'auto',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 28px',
    background: '#198754',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 28px',
    background: '#fff',
    border: '2px solid #dee2e6',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    color: '#495057',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
};

export default FormationDetail;
